// TVA — déclarations de TVA avec soumission
import { useState, useCallback, useMemo } from "react";
import { FileText, AlertCircle, CheckCircle, Send } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { KPICard } from "@/components/admin/shared/KPICard";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { useTVAReport, useSubmitTVA } from "@/hooks/admin/useFinance";
import type { TVAStatus } from "@/lib/admin-api/finance";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<TVAStatus, string> = {
  draft: "bg-stone-100 text-stone-700 border-stone-200",
  submitted: "bg-amber-100 text-amber-800 border-amber-200",
  paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const STATUS_LABELS: Record<TVAStatus, string> = {
  draft: "Brouillon",
  submitted: "Soumise",
  paid: "Payée",
};

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatFcfa(amount: number): string {
  return `${amount.toLocaleString("fr-SN")} FCFA`;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function TVA() {
  const [periodFilter, setPeriodFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<TVAStatus | "">("");

  const params = useMemo(
    () => ({
      period: periodFilter || null,
      status: statusFilter || null,
    }),
    [periodFilter, statusFilter],
  );

  const { data, isLoading, isError, refetch } = useTVAReport(params);
  const submitTVA = useSubmitTVA();

  const entries = data?.entries ?? [];

  const totalCollected = useMemo(
    () => entries.reduce((sum, e) => sum + e.tva_collected_fcfa, 0),
    [entries],
  );
  const totalDeductible = useMemo(
    () => entries.reduce((sum, e) => sum + e.tva_deductible_fcfa, 0),
    [entries],
  );
  const pendingCount = useMemo(
    () => entries.filter((e) => e.status === "draft").length,
    [entries],
  );

  const handleSubmit = useCallback(
    (id: string) => {
      submitTVA.mutate(id);
    },
    [submitTVA],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Déclarations TVA
            </h1>
            <p className="text-sm text-stone-500">
              {isLoading ? "Chargement…" : `${entries.length} déclaration(s)`}
            </p>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KPICard
            label="TVA collectée"
            value={totalCollected}
            format="currency"
            icon={<FileText className="h-4 w-4" />}
          />
          <KPICard
            label="TVA déductible"
            value={totalDeductible}
            format="currency"
            icon={<CheckCircle className="h-4 w-4" />}
          />
          <KPICard
            label="TVA nette due"
            value={data?.total_due_fcfa ?? 0}
            format="currency"
            icon={<AlertCircle className="h-4 w-4" />}
          />
          <KPICard
            label="Déclarations en attente"
            value={pendingCount}
            format="number"
            icon={<Send className="h-4 w-4" />}
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="month"
            value={periodFilter}
            onChange={(e) => setPeriodFilter(e.target.value)}
            className="h-9 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as TVAStatus | "")}
            className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            <option value="">Tous les statuts</option>
            <option value="draft">Brouillon</option>
            <option value="submitted">Soumise</option>
            <option value="paid">Payée</option>
          </select>
          {(periodFilter || statusFilter) && (
            <button
              type="button"
              onClick={() => {
                setPeriodFilter("");
                setStatusFilter("");
              }}
              className="text-sm text-stone-500 hover:text-stone-700"
            >
              Réinitialiser
            </button>
          )}
        </div>

        {/* Mutation feedback */}
        {submitTVA.isSuccess && (
          <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            <CheckCircle className="h-4 w-4 shrink-0" />
            <span>Déclaration soumise avec succès.</span>
          </div>
        )}
        {submitTVA.isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de soumettre la déclaration.</span>
          </div>
        )}

        {/* Error state */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les déclarations TVA.</span>
            <button
              type="button"
              onClick={() => refetch()}
              className="ml-auto rounded border border-red-200 bg-white px-3 py-1 text-xs text-red-700 hover:bg-red-50"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Table */}
        {!isError && (
          <>
            {isLoading && (
              <div className="overflow-hidden rounded-lg border border-stone-200">
                <table className="w-full">
                  <thead className="bg-stone-50">
                    <tr>
                      {["Période", "Base taxable", "TVA collectée", "TVA déductible", "TVA due", "Statut", "Actions"].map(
                        (h) => (
                          <th
                            key={h}
                            className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500"
                          >
                            {h}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100 bg-white">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 7 }).map((__, j) => (
                          <td key={j} className="px-4 py-3">
                            <div className="h-4 w-full animate-pulse rounded bg-stone-100" />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!isLoading && entries.length === 0 && (
              <EmptyState
                icon={<FileText className="h-6 w-6" />}
                title="Aucune déclaration TVA"
                description="Les déclarations de TVA apparaîtront ici."
              />
            )}

            {!isLoading && entries.length > 0 && (
              <div className="overflow-x-auto">
                <div className="overflow-hidden rounded-lg border border-stone-200">
                  <table className="w-full">
                    <thead className="bg-stone-50">
                      <tr>
                        {[
                          "Période",
                          "Base taxable",
                          "TVA collectée",
                          "TVA déductible",
                          "TVA nette due",
                          "Statut",
                          "Actions",
                        ].map((h) => (
                          <th
                            key={h}
                            className="whitespace-nowrap px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100 bg-white">
                      {entries.map((e) => (
                        <tr
                          key={e.id}
                          className={
                            e.status === "draft" && e.tva_due_fcfa > 0
                              ? "bg-red-50/40 hover:bg-red-50/60"
                              : "hover:bg-stone-50"
                          }
                        >
                          <td className="px-4 py-3 text-sm font-medium text-stone-900">
                            {e.period}
                          </td>
                          <td className="px-4 py-3 text-sm text-stone-700">
                            {formatFcfa(e.taxable_base_fcfa)}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-stone-900">
                            {formatFcfa(e.tva_collected_fcfa)}
                          </td>
                          <td className="px-4 py-3 text-sm text-emerald-700">
                            {formatFcfa(e.tva_deductible_fcfa)}
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-stone-900">
                            {formatFcfa(e.tva_due_fcfa)}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[e.status]}`}
                            >
                              {STATUS_LABELS[e.status]}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {e.status === "draft" ? (
                              <button
                                type="button"
                                onClick={() => handleSubmit(e.id)}
                                disabled={submitTVA.isPending}
                                className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                              >
                                <Send className="h-3.5 w-3.5" />
                                Soumettre
                              </button>
                            ) : (
                              <span className="text-xs text-stone-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
